
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { runWorkerTask } from '../services/analysisService';
import { findSongGrid } from '../services/gscLibrary';
import { parseYouTubeUrl } from '../services/youtubeService';
import { YouTubePlayer } from './youtube/YouTubePlayer';
import { YouTubeSegmentSelector } from './youtube/YouTubeSegmentSelector';
import { YouTubeReference } from '../types';
import { parseGscFile, base64ToBlob } from '../services/gscFormatService';

interface StemImportProps {
  onImport: (file: File | YouTubeReference, bpm: number, grid: string, offset?: number) => void;
  onCancel: () => void;
  lang: 'en' | 'fr';
}

export const StemImport: React.FC<StemImportProps> = ({ onImport, onCancel, lang }) => {
  const [phase, setPhase] = useState<'SOURCE' | 'PROCESSING' | 'CALIBRATE' | 'YOUTUBE_MODE'>('SOURCE');
  const [importMode, setImportMode] = useState<'local' | 'web' | 'gsc'>('local');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [webUrl, setWebUrl] = useState('');
  const [progressMsg, setProgressMsg] = useState("");
  const [manualBpm, setManualBpm] = useState(120);
  const [loopStart, setLoopStart] = useState(0);
  const [loopEnd, setLoopEnd] = useState(15);
  const [ytVideoId, setYtVideoId] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [extractedData, setExtractedData] = useState<any | null>(null);

  const handleGscUpload = async (file: File) => {
      setPhase('PROCESSING');
      setProgressMsg(lang === 'fr' ? "Décodage package..." : "Decoding package...");
      try {
          const pkg = await parseGscFile(file);
          const refBlob = base64ToBlob(pkg.audio.referenceBase64, pkg.audio.mimeType);
          const refFile = new File([refBlob], `${pkg.metadata.title}_ref.wav`, { type: pkg.audio.mimeType });
          onImport(refFile, pkg.metadata.bpm, pkg.metadata.grid, 0);
      } catch (e) { alert("Erreur .gsc"); setPhase('SOURCE'); }
  };

  const handleProcess = async () => {
    const videoId = parseYouTubeUrl(webUrl);
    if (importMode === 'web' && videoId) {
        setYtVideoId(videoId); 
        setLoopStart(0); 
        setLoopEnd(15); 
        setPhase('YOUTUBE_MODE'); 
        return;
    }
    
    setPhase('PROCESSING');
    setProgressMsg(lang === 'fr' ? "Chargement Master..." : "Loading Master...");
    try {
        const tempCtx = new AudioContext();
        const arrayBuf = importMode === 'local' && selectedFile ? await selectedFile.arrayBuffer() : await (await fetch(webUrl)).arrayBuffer();
        const audioBuf = await tempCtx.decodeAudioData(arrayBuf);
        const result = await runWorkerTask<any>('EXTRACT_STEM', { data: audioBuf.getChannelData(0), sampleRate: tempCtx.sampleRate });
        setExtractedData({
            file: selectedFile || new File([], "web_track.wav"), 
            bpm: result.bpm, 
            grid: findSongGrid(selectedFile?.name || "")?.join(' ') || "G C | D | G",
            totalDuration: audioBuf.duration
        });
        setManualBpm(result.bpm);
        setLoopStart(result.firstOnset); 
        setLoopEnd(Math.min(result.firstOnset + 15, audioBuf.duration));
        setPhase('CALIBRATE');
        await tempCtx.close();
    } catch (e) { setPhase('SOURCE'); alert("Erreur."); }
  };

  const handleFinalImport = () => {
    if (ytVideoId) {
        const ref: YouTubeReference = { 
          kind: 'YOUTUBE', 
          videoId: ytVideoId, 
          startSec: loopStart, 
          endSec: loopEnd, 
          bpmHint: manualBpm, 
          title: "YouTube Loop" 
        };
        onImport(ref, manualBpm, "G C | D | G", 0);
    } else if (extractedData) {
        onImport(extractedData.file, manualBpm, extractedData.grid, loopStart);
    }
  };

  return (
    <div className="p-8 flex flex-col items-center justify-center h-full text-center space-y-8 bg-smart-dark/95 backdrop-blur-3xl rounded-[48px] m-4 md:m-10 shadow-2xl relative overflow-hidden">
      {phase === 'SOURCE' && (
          <div className="w-full max-w-2xl space-y-10">
            <div className="space-y-2">
              <h2 className="text-4xl md:text-5xl font-black italic uppercase text-white tracking-tighter">Snippet <span className="text-smart-accent">Extractor</span></h2>
              <p className="text-slate-500 font-medium italic text-sm uppercase tracking-widest">Préparez votre zone de travail</p>
            </div>

            <div className="flex bg-black/40 p-1.5 rounded-2xl border border-white/10 w-fit mx-auto">
                <button onClick={() => setImportMode('local')} className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${importMode === 'local' ? 'bg-white text-black shadow-lg' : 'text-slate-500'}`}>Fichier</button>
                <button onClick={() => setImportMode('gsc')} className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${importMode === 'gsc' ? 'bg-white text-black shadow-lg' : 'text-slate-500'}`}>.GSC</button>
                <button onClick={() => setImportMode('web')} className={`px-6 py-3 rounded-xl text-[10px] font-black uppercase transition-all ${importMode === 'web' ? 'bg-white text-black shadow-lg' : 'text-slate-500'}`}>YouTube</button>
            </div>

            <div className="p-1 min-h-[200px] flex items-center justify-center">
              {importMode === 'web' ? (
                  <div className="w-full p-8 md:p-12 rounded-[40px] border-2 border-dashed border-red-500/20 bg-red-500/5 flex flex-col items-center gap-6 animate-fade-in">
                      <input 
                        type="text" 
                        value={webUrl} 
                        onChange={(e) => setWebUrl(e.target.value)} 
                        placeholder="Coller l'URL YouTube (ex: youtu.be/...)" 
                        className="w-full bg-black/60 border-2 border-white/10 focus:border-red-500/50 transition-colors rounded-2xl px-6 py-5 text-white text-center outline-none font-bold" 
                      />
                  </div>
              ) : (
                  <button onClick={() => fileInputRef.current?.click()} className="w-full p-12 md:p-20 rounded-[40px] border-2 border-dashed border-white/10 bg-black/40 hover:border-smart-accent transition-all flex flex-col items-center gap-4 group animate-fade-in">
                      <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center text-4xl group-hover:scale-110 transition-transform">📁</div>
                      <span className="text-sm font-black text-slate-400 group-hover:text-white uppercase tracking-widest">
                        {selectedFile ? selectedFile.name : "Cliquez pour charger un Master"}
                      </span>
                  </button>
              )}
            </div>

            <input type="file" ref={fileInputRef} onChange={e => { const f = e.target.files?.[0]; if (f?.name.endsWith('.gsc')) handleGscUpload(f); else setSelectedFile(f || null); }} className="hidden" accept={importMode === 'gsc' ? ".gsc" : "audio/*,video/*"} />
            
            <div className="flex gap-4">
                <button onClick={onCancel} className="flex-1 py-6 rounded-2xl border border-white/10 text-slate-500 font-black uppercase text-xs hover:bg-white/5">Annuler</button>
                <button onClick={handleProcess} disabled={(!selectedFile && importMode !== 'web') || (importMode === 'web' && !webUrl)} className="flex-[2] py-6 rounded-2xl bg-white text-black font-black uppercase text-xs tracking-widest shadow-2xl hover:scale-105 active:scale-95 disabled:opacity-20 transition-all">Scanner le Flux 🧬</button>
            </div>
          </div>
      )}

      {phase === 'YOUTUBE_MODE' && ytVideoId && (
          <div className="w-full max-w-6xl space-y-8 animate-fade-in-up flex flex-col h-[85vh]">
              <div className="flex justify-between items-end bg-black/40 p-6 rounded-[40px] border border-white/10 shrink-0">
                  <div className="text-left">
                    <h3 className="text-3xl font-black italic uppercase text-white leading-none">Loop <span className="text-red-500">Selector</span></h3>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">Éditez votre segment de Play-Along</p>
                  </div>
                  <div className="text-right">
                    <span className="text-[8px] font-black text-slate-600 uppercase block">YT_ID: {ytVideoId}</span>
                  </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 flex-1 min-h-0">
                  <div className="space-y-6 flex flex-col">
                    <YouTubePlayer videoId={ytVideoId} startSec={loopStart} className="flex-1 rounded-[40px] shadow-3xl overflow-hidden" />
                    <div className="bg-white/[0.03] p-8 rounded-[40px] border border-white/5 space-y-4">
                           <div className="flex justify-between items-center px-2">
                             <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Tempo Master</label>
                             <span className="text-2xl font-black text-white italic drop-shadow-lg">{manualBpm} BPM</span>
                           </div>
                           <input type="range" min="40" max="240" step="1" value={manualBpm} onChange={e => setManualBpm(parseInt(e.target.value))} className="w-full h-2 accent-smart-gold bg-white/10 rounded-full appearance-none cursor-pointer" />
                    </div>
                  </div>
                  <div className="h-full">
                      <YouTubeSegmentSelector 
                        start={loopStart} 
                        end={loopEnd} 
                        maxDuration={600} 
                        onChange={(s,e) => { setLoopStart(s); setLoopEnd(e); }}
                        onSeek={(t) => {}} // Le player YouTube réagit déjà au changement de start
                      />
                  </div>
              </div>

              <div className="flex gap-4 shrink-0">
                  <button onClick={() => setPhase('SOURCE')} className="flex-1 py-7 rounded-[32px] border border-white/10 text-slate-500 font-black uppercase text-xs hover:bg-white/5 transition-all">Source</button>
                  <button onClick={handleFinalImport} className="flex-[3] py-7 rounded-[32px] bg-white text-black font-black uppercase text-sm tracking-[0.4em] shadow-[0_20px_50px_rgba(255,255,255,0.2)] hover:scale-[1.02] active:scale-95 transition-all">
                    Lancer le Play-Along 🎸
                  </button>
              </div>
          </div>
      )}

      {phase === 'PROCESSING' && (
          <div className="flex flex-col items-center gap-8 py-20">
              <div className="w-32 h-32 border-[6px] border-smart-gold border-t-transparent rounded-full animate-spin shadow-[0_0_60px_#DA900033]"></div>
              <div className="space-y-2">
                <p className="text-3xl font-black text-white uppercase italic animate-pulse tracking-tighter">{progressMsg}</p>
                <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.5em]">Neural Analysis Pipeline</p>
              </div>
          </div>
      )}

      {phase === 'CALIBRATE' && extractedData && (
          <div className="w-full max-w-5xl space-y-10 animate-fade-in-up">
              <div className="p-10 bg-black/60 rounded-[64px] border border-white/10 shadow-3xl space-y-8">
                  <div className="text-center space-y-2">
                    <h3 className="text-4xl font-black text-white italic uppercase tracking-tighter">Snippet <span className="text-cyan-400">Settings</span></h3>
                    <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Ajustez le tempo et le début du morceau</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="space-y-4 bg-white/[0.03] p-8 rounded-[40px] border border-white/5">
                          <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">Timing Offset</span>
                          <div className="flex flex-col gap-2">
                            <input type="number" step="0.01" value={loopStart} onChange={e => setLoopStart(parseFloat(e.target.value))} className="w-full bg-black/40 p-6 rounded-3xl border-2 border-white/10 text-3xl font-black text-white text-center outline-none focus:border-cyan-500" />
                            <span className="text-[8px] text-slate-500 uppercase font-black">Secondes depuis le début</span>
                          </div>
                      </div>
                      <div className="space-y-4 bg-white/[0.03] p-8 rounded-[40px] border border-white/5">
                          <span className="text-[10px] font-black text-smart-gold uppercase tracking-widest">BPM Détecté</span>
                          <div className="flex flex-col gap-2">
                            <input type="number" value={manualBpm} onChange={e => setManualBpm(parseInt(e.target.value))} className="w-full bg-black/40 p-6 rounded-3xl border-2 border-white/10 text-3xl font-black text-smart-gold text-center outline-none focus:border-smart-gold" />
                            <span className="text-[8px] text-slate-500 uppercase font-black">Battements par minute</span>
                          </div>
                      </div>
                  </div>
              </div>
              <div className="flex gap-4">
                  <button onClick={() => setPhase('SOURCE')} className="flex-1 py-7 rounded-[32px] border border-white/10 text-slate-500 font-black uppercase text-xs">Source</button>
                  <button onClick={handleFinalImport} className="flex-[3] py-7 rounded-[32px] bg-white text-black font-black uppercase text-sm tracking-[0.4em] shadow-2xl hover:scale-[1.02] active:scale-95 transition-all">
                    Utiliser ce riff ⚡
                  </button>
              </div>
          </div>
      )}
    </div>
  );
};
